import { createAsyncThunk } from '@reduxjs/toolkit';
import { getShopifyData } from "../../lib/dynamicQueries/config";

/**
 * GraphQL query to fetch bundle metaobjects
 */
const GET_BUNDLES_QUERY = `{
  metaobjects(first: 250, type: "bundles") {
    edges {
      node {
        id
        type
        products: field(key: "product_bundle_selector") {
          value
        }
        title: field(key: "product_bundle_builder") {
          value
        }
        productTitle: field(key: "product_page_title") {
          value
        }
        description: field(key: "description") {
          value
        }
        overview: field(key: "overview") {
          value
        }
        image: field(key: "bundle_image") {
          reference {
            ...on MediaImage {
              image {
                altText
                url
              }
            }
          }
        }
        visible: field(key: "display_bundle_in_nz") {
          value
        }
        bundle_price_nz: field(key: "bundle_price_nz") {
          value
        }
        quantity: field(key: "quantity") {
          value
        }
        buttonText: field(key: "cart_button_text") {
          value
        }
      }
    }
  }
}`;

/**
 * Async thunk to fetch bundles from Shopify
 */
export const fetchBundles = createAsyncThunk(
    'bundles/fetchBundles',
    async (_, { rejectWithValue }) => {
      try {
        const response = await getShopifyData(GET_BUNDLES_QUERY);
        return response.data.metaobjects.edges.map(edge => edge.node);
      } catch (error) {
        return rejectWithValue(error.message);
      }
    }
);

/**
 * Parse bundle products from metaobject data
 * @param {Object} bundleMetaobject - Bundle metaobject from Shopify
 * @returns {Array} Array of product variants in the bundle
 */
export function parseBundleProducts(bundleMetaobject) {
  if (!bundleMetaobject || !bundleMetaobject.products || !bundleMetaobject.products.value) {
    return [];
  }

  try {
    const productData = JSON.parse(bundleMetaobject.products.value);

    return productData.map(product => ({
      id: product,
      quantity: parseInt(bundleMetaobject.quantity.value)
    }));
  } catch (error) {
    console.error('Error parsing bundle products:', error);
    return [];
  }
}

/**
 * Detects possible bundles from individual items in cart
 * @param {Array} items - Cart items
 * @param {Array} availableBundles - All available bundle metaobjects from the store
 * @returns {Object} Detected bundles and remaining items
 */
export function detectBundlesFromItems(items, availableBundles) {
  if (!availableBundles || !availableBundles.length || !items || !items.length) {
    return { detectedBundles: [], remainingItems: [...items] };
  }

  let remainingItems = JSON.parse(JSON.stringify(items));
  const detectedBundles = [];

  availableBundles.forEach(bundleMetaobject => {
    if (bundleMetaobject.visible && bundleMetaobject.visible.value === 'false') {
      // return;
    }

    const requiredProducts = parseBundleProducts(bundleMetaobject);

    if (requiredProducts.length === 0) {
      return;
    }

    let continueChecking = true;
    while (continueChecking) {
      const bundleItems = [];
      let canFormBundle = true;

      for (const requiredProduct of requiredProducts) {
        const matchIndex = remainingItems.findIndex(item =>
            item.merchandise.product.id === requiredProduct.id &&
            item.quantity >= requiredProduct.quantity
        );

        if (matchIndex >= 0) {
          bundleItems.push({
            item: remainingItems[matchIndex],
            index: matchIndex,
            requiredQuantity: requiredProduct.quantity
          });
        } else {
          canFormBundle = false;
          break;
        }
      }

      if (canFormBundle) {
        bundleItems.sort((a, b) => b.index - a.index);

        bundleItems.forEach(({ index, requiredQuantity }) => {
          if (remainingItems[index].quantity === requiredQuantity) {
            remainingItems.splice(index, 1);
          } else {
            remainingItems[index] = {
              ...remainingItems[index],
              quantity: remainingItems[index].quantity - requiredQuantity
            };
          }
        });

        const bundlePrice = JSON.parse(bundleMetaobject.bundle_price_nz?.value);
        if (!bundlePrice.amount) {
          break;
        }

        detectedBundles.push({
          bundle: {
            id: bundleMetaobject.id,
            title: bundleMetaobject.title?.value || "Bundle",
            bundlePrice: parseFloat(bundlePrice.amount),
            image: bundleMetaobject.image?.reference?.image || null,
          },
          requiredProducts,
          quantity: 1,
          autoDetected: true
        });
      } else {
        continueChecking = false;
      }
    }
  });

  return { detectedBundles, remainingItems };
}

/**
 * Calculate total cost with auto-bundling applied
 * @param {Array} items - Cart items
 * @param {Array} explicitBundles - Explicitly added bundles
 * @param {Array} availableBundles - All available bundle definitions
 * @returns {Object} Object containing cost info and detected bundles
 */
export function calculateTotalWithBundles(items, explicitBundles = [], availableBundles = []) {
  const { detectedBundles, remainingItems } = detectBundlesFromItems(items, availableBundles);

  const allBundles = [...explicitBundles, ...detectedBundles];

  const itemsCost = remainingItems.reduce(
      (totalCost, item) =>
          item.merchandise.compareAtPrice
              ? totalCost + Number(item.merchandise.compareAtPrice.amount) * item.quantity
              : totalCost + Number(item.merchandise.price.amount) * item.quantity,
      0
  );

  const bundlesCost = allBundles.reduce((totalCost, bundle) => {
    const componentsPrice = bundle.requiredProducts
        ? bundle.requiredProducts.reduce((total, product) => {
          // Find the product in original items to get price
          const originalItem = items.find(item => item.merchandise.product.id === product.id);
          if (originalItem) {
            const itemPrice = originalItem.merchandise.price.amount;
            return total + (parseFloat(itemPrice) * product.quantity);
          }
          return total;
        }, 0)
        : 0;

    return totalCost + componentsPrice * bundle.quantity;
  }, 0);

  let bundleDiscount = 0;
  detectedBundles.forEach(bundle => {
    if (bundle.bundle.bundlePrice) {
      // Calculate regular price of all items in the bundle
      const regularPrice = bundle.requiredProducts.reduce((total, product) => {
        // Find the product in original items to get price
        const originalItem = items.find(item => item.merchandise.product.id === product.id);
        if (originalItem) {
          const itemPrice = originalItem.merchandise.price.amount;
          return total + (parseFloat(itemPrice) * product.quantity);
        }
        return total;
      }, 0);

      bundleDiscount += (regularPrice - bundle.bundle.bundlePrice) * bundle.quantity;
    }
  });

  let itemsDiscount = remainingItems.reduce((total, item) => {
    if (
        item.merchandise.compareAtPrice &&
        item.merchandise.price.amount !== item.merchandise.compareAtPrice?.amount
    ) {
      return total +
          (item.merchandise.compareAtPrice.amount - item.merchandise.price.amount) *
          item.quantity;
    }
    return total;
  }, 0);

  return {
    totalCost: itemsCost + bundlesCost,
    itemsCost,
    bundlesCost,
    totalDiscount: bundleDiscount + itemsDiscount,
    bundleDiscount,
    itemsDiscount,
    detectedBundles,
    remainingItems
  };
}

/**
 * Applies a proportional discount to each variant in a bundle to match the bundle price
 * @param {Array} productVariants - Array of variant objects in the bundle
 * @param {number} bundlePrice - The target bundle price
 * @returns {Array} Array of variants with bundleDiscount added
 */
export function applyDiscountToMatchBundlePrice(productVariants, bundlePrice) {
  const totalRetailPrice = productVariants.reduce(
      (acc, variant) => acc + parseFloat(variant.price.amount),
      0
  );

  let totalDiscountNeeded = totalRetailPrice - bundlePrice;

  if (totalDiscountNeeded <= 0 || isNaN(totalDiscountNeeded)) {
    totalDiscountNeeded = 0;
  }

  return productVariants.map(variant => {
    const variantPrice = parseFloat(variant.price.amount);
    const proportionalDiscount = (variantPrice / totalRetailPrice) * totalDiscountNeeded;

    const bundleDiscount = Math.round(proportionalDiscount * 100) / 100;

    return {
      ...variant,
      bundleDiscount,
      bundlePrice: variantPrice - bundleDiscount
    };
  });
}

/**
 * Gets the bundle discount for a specific variant in the cart
 * @param {Object} item - Cart item
 * @param {Array} autoBundles - Auto-detected bundles in the cart
 * @param {Array} items - All items in the cart (needed to find other bundle items)
 * @returns {Object|null} Bundle discount info or null if not in a bundle
 */
export function getBundleDiscountForItem(item, autoBundles, items) {
  if (!autoBundles || !autoBundles.length) return null;

  const bundle = autoBundles.find(bundle => {
    return bundle.requiredProducts.some(product => product.id === item.merchandise.product.id)
  });

  if (!bundle || !bundle.bundle.bundlePrice) return null;

  const bundleVariants = bundle.requiredProducts.map(product => {
    const matchingItem = items.find(i => i.merchandise.product.id === product.id);

    if (!matchingItem) return null;

    return {
      id: product.id,
      price: matchingItem.merchandise.price,
      quantity: product.quantity
    };
  }).filter(Boolean);

  if (!bundleVariants.length) return null;

  const variantsWithDiscount = applyDiscountToMatchBundlePrice(
      bundleVariants,
      bundle.bundle.bundlePrice
  );

  const thisVariant = variantsWithDiscount.find(v => v.id === item.merchandise.product.id);

  if (!thisVariant) return null;

  return {
    originalPrice: parseFloat(item.merchandise.price.amount),
    bundlePrice: thisVariant.bundlePrice,
    discount: thisVariant.bundleDiscount,
    discountPercentage: Math.round(
        (thisVariant.bundleDiscount / parseFloat(item.merchandise.price.amount)) * 100
    ),
    bundleTitle: bundle.bundle.title || "Bundle"
  };
}

/**
 * Checks if a product is part of any available bundles
 * @param {string} productId - The product ID to check
 * @param {Array} availableBundles - All available bundle definitions
 * @returns {Object|null} Bundle object if product is part of a bundle, otherwise null
 */
export function isProductInBundle(productId, availableBundles = []) {
  if (true || !availableBundles || !availableBundles.length || !productId) {
    return null;
  }

  return availableBundles.find(bundle => {
    const bundleProducts = JSON.parse(bundle?.products.value);
    return bundleProducts &&
        Array.isArray(bundleProducts) &&
        bundleProducts.includes(productId);
  }) || null;
}

export default {
  fetchBundles,
  parseBundleProducts,
  detectBundlesFromItems,
  calculateTotalWithBundles,
  applyDiscountToMatchBundlePrice,
  getBundleDiscountForItem,
  isProductInBundle,
};
